import { Component } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "redux";
import isEmpty from "lodash/isEmpty";

import AssessmentsNoResults from "../../../../views/library/assessments/Overview/AssessmentsNoResults";
import SearchInput from "../../../../components/searchInput/SearchInput";
import AssessmentOverviewHeader from "./AssessmentOverviewHeader/AssessmentOverviewHeader";
import Restricted from "../../../Application/Restricted";
import RtnEventsEmitter from "../../../Application/services/realTimeNotification/rtnEventsEmitter";
import DeleteContentConfirmationModal, {
  type RemoveContentObserverInput,
} from "../../../../components/deleteContentConfirmationModal/DeleteContentConfirmationModal";
import Observable from "../../../../utils/Observable";
import GenericItemsView from "../../../../views/ItemsView/GenericItemsView";

import { type AssessmentsOverviewOwnProps, type AssessmentsOverviewState } from "./types";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { getColumnOptions } from "./getColumnOptions";
import { AssessmentFilterForm } from "../../../../components/filterForms";
import { ContentTypesEnum, RolePermissions, RouteNames, type SortingDirection } from "../../../../enums";
import {
  assessmentsStateSelector,
  assessmentFilterOptionsSelector,
  assessmentSearchSelector,
} from "../state/selectors";
import {
  changeVisibility,
  deleteAssessments,
  duplicateAssessments,
  loadAssessments,
} from "../state/thunks/assessmentsOverviewThunk";
import {
  AssessmentEditVisibilityAllSuccess,
  AssessmentLockSuccess,
} from "../../../Application/services/realTimeNotification/events/library/libraryEvents";
import { bindAction, type IObservable } from "../../../../interfaces";
import { CreateAssessmentButton } from "./AssessmentOverviewHeader/CreateAssessmentButton";
import {
  resetAppliedFilter,
  setAppliedFilter,
  setSearch,
  resetSearch,
  resetShouldSort,
  setShouldSort,
  setSortingColumnName,
  setSortingDirection,
  resetSortingColumnName,
  resetSortingDirection,
} from "../state/slices/assessmentFiltersSlice";
import { getOverviewFilterOptions } from "../state/thunks/assessmentFiltersThunk";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "../../../../interfaces/onSelectionChanged";
import { type AssessmentOverview } from "../types/state";
import { withRouter } from "../../../../adapters/withRouter/withRouter";

import * as assessmentsOverviewActions from "../state/actions/assessmentsOverviewActions";
import * as assessmentEntityStateActions from "../state/actions/assessmentEntityStateActions";
import * as backgroundTasksActions from "../../../BackgroundTasks/state/backgroundTasksActions";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { FeatureFlags } from "featureFlags";
import { type LDProps } from "../../../LDProps";
import { AssetVisibilityConfirmationModal } from "components/assetVisibilityConfirmationModal/AssetVisibilityConfirmationModal";

export type AssessmentsOverviewProps = AssessmentsOverviewOwnProps & PropsFromRedux & LDProps;

export class AssessmentsOverview extends Component<AssessmentsOverviewProps, AssessmentsOverviewState> {
  private purchasedSelectedCount = 0;
  private undeletableSelectedCount = 0;
  private draftSelectedCount = 0;
  private hiddenSelectedCount = 0;
  private visibleSelectedCount = 0;
  private unpublishedSelectedCount = 0;
  private readonly deleteContentWithDependenciesFeatureEnabled: boolean;
  private readonly onTriggerAssessmentRemovalObserver: IObservable<
    (onRemoveConfirm: () => void, removeContentInput: RemoveContentObserverInput) => void
  >;
  private readonly onTriggerChangeVisibilityObserver: IObservable<
    (onConfirm: () => void, visible: boolean, ids: number[]) => void
  >;

  constructor(props: AssessmentsOverviewProps) {
    super(props);
    this.state = {
      selectedAssessmentsIds: [],
      purchasedSelected: false,
      undeletableSelected: false,
      draftSelected: false,
      searchHasValue: false,
      hiddenSelected: false,
      visibleSelected: false,
      unpublishedSelected: false,
    };
    this.onTriggerAssessmentRemovalObserver = new Observable<
      (onRemoveConfirm: () => void, removeContentInput: RemoveContentObserverInput) => void
    >();
    this.onTriggerChangeVisibilityObserver = new Observable<
      (onConfirm: () => void, visible: boolean, ids: number[]) => void
    >();
    this.deleteContentWithDependenciesFeatureEnabled =
      !!this.props.flags?.[FeatureFlags.DeleteContentWithDependenciesFeature];
  }

  componentDidMount() {
    RtnEventsEmitter.subscribe(AssessmentLockSuccess, this.goToAssessmentEdit);
    RtnEventsEmitter.subscribe(AssessmentEditVisibilityAllSuccess, this.refetchItemsFromStart);
  }

  componentWillUnmount() {
    RtnEventsEmitter.unsubscribe(AssessmentLockSuccess, this.goToAssessmentEdit);
    RtnEventsEmitter.unsubscribe(AssessmentEditVisibilityAllSuccess, this.refetchItemsFromStart);
    if (!window.location.pathname.includes(`/${RouteNames.assessments}`)) {
      this.props.reset();
    }
  }

  reloadListItems?: (enableSorting: boolean) => void;

  getFilterOptions = () => {
    const { filterOptions, getFilterOptions } = this.props;

    if (isEmpty(filterOptions)) {
      getFilterOptions();
    }
  };

  onSelectedItemsChanged = (ids: Array<number>) => {
    this.setState({ selectedAssessmentsIds: [...(ids ?? [])] });
  };

  onClearedSelection = () => {
    this.purchasedSelectedCount = this.undeletableSelectedCount = this.draftSelectedCount = 0;
    this.hiddenSelectedCount = 0;
    this.visibleSelectedCount = 0;

    this.setState({
      purchasedSelected: false,
      undeletableSelected: false,
      draftSelected: false,
      hiddenSelected: false,
      visibleSelected: false,
      unpublishedSelected: false,

      selectedAssessmentsIds: [],
    });
  };

  onSelectionChanged = (args: SelectionChangedArgs<AssessmentOverview>) => {
    const onAdded = (assessment: AssessmentOverview) => {
      assessment.isPurchased && ++this.purchasedSelectedCount;
      !assessment.canBeDeleted && ++this.undeletableSelectedCount;
      assessment.isDraft && ++this.draftSelectedCount;
      !assessment.visibility && ++this.hiddenSelectedCount;
      assessment.visibility && ++this.visibleSelectedCount;
      !assessment.hasBeenPublished && ++this.unpublishedSelectedCount;
    };
    const onRemoved = (assessment: AssessmentOverview) => {
      assessment.isPurchased && --this.purchasedSelectedCount;
      !assessment.canBeDeleted && --this.undeletableSelectedCount;
      assessment.isDraft && --this.draftSelectedCount;
      !assessment.visibility && --this.hiddenSelectedCount;
      assessment.visibility && --this.visibleSelectedCount;
      !assessment.hasBeenPublished && --this.unpublishedSelectedCount;
    };

    HandleOnSelectionChanged(args, onAdded, onRemoved, this.onClearedSelection);

    this.setState({
      purchasedSelected: this.purchasedSelectedCount > 0,
      undeletableSelected: this.undeletableSelectedCount > 0,
      draftSelected: this.draftSelectedCount > 0,
      hiddenSelected: this.hiddenSelectedCount > 0,
      visibleSelected: this.visibleSelectedCount > 0,
      unpublishedSelected: this.unpublishedSelectedCount > 0,
    });
  };

  createReloadListMethod = (reloadListItems: (enableSorting: boolean) => void) => {
    this.reloadListItems = reloadListItems;
  };

  refetchItemsFromStart = (enableSorting: boolean) => {
    this.reloadListItems && this.reloadListItems(enableSorting);
  };

  onSearchChange = (search: string) => {
    this.props.setSearch(search);
    this.props.setShouldSort(search === "");
    // Wait for next tick so the table header updates
    // before running the fetch
    setTimeout(() => {
      this.refetchItemsFromStart(isEmpty(search));
    }, 0);
  };

  renderSearchInput = (): React.ReactElement => (
    <SearchInput
      placeholder="Search for Assessments..."
      onChange={this.onSearchChange}
      defaultValue={this.props.search}
    />
  );

  goToAssessmentEdit = (payload: { id: number }) => this.props.navigate(`${payload.id.toString()}/configuration`);

  handleEditClick = (id: number, isDraft?: boolean) => () => {
    if (!isDraft) this.props.entityStateActions.fetchDraftAssessmentEntity(id);
    this.goToAssessmentEdit({ id });
  };

  handleDuplicateClick = (ids: number[]) => () => {
    this.onClearedSelection();
    this.props.duplicateAssessments(ids, this.props.loadAssessments);
  };

  handleDeleteClick = (ids: number[], flowsCount?: number, packsCount?: number) => () => {
    this.onTriggerAssessmentRemovalObserver.notify(
      () => {
        this.props.deleteAssessments(ids, this.props.loadAssessments);
        this.onClearedSelection();
      },
      {
        selectedItemsCount: ids.length,
        flowsCount,
        packsCount,
      },
    );
  };

  handleToggleVisibility = (ids: number[], visible: boolean) => () => {
    this.onTriggerChangeVisibilityObserver.notify(
      () => {
        this.props.toggleVisibility(ids, visible);
        this.onClearedSelection();
      },
      visible,
      ids,
    );
  };

  loadAssessmentList = (
    skip?: number,
    top?: number,
    sortingColumnName?: string,
    sortingDirection?: SortingDirection,
  ) => {
    let column = sortingColumnName,
      sortDirection: string | undefined = sortingDirection;
    if (!this.props.shouldSort) {
      column = "";
      sortDirection = "";
    }
    this.props.loadAssessments(skip, top, column, sortDirection as SortingDirection);
    this.props.setShouldSort(true);
  };

  render() {
    const { assessments, totalCount, isLoading, filterOptions, appliedFilter, isFilterLoading } = this.props;
    return (
      <section className="nested-content assessments">
        <AssessmentOverviewHeader
          selectedIds={this.state.selectedAssessmentsIds}
          purchasedSelected={this.state.purchasedSelected}
          undeletableSelected={this.state.undeletableSelected}
          draftSelected={this.state.draftSelected}
          onDuplicate={this.handleDuplicateClick}
          deletionHandler={this.handleDeleteClick}
          clearSelection={this.onClearedSelection}
          onToggleVisibility={this.handleToggleVisibility}
          hiddenSelected={this.state.hiddenSelected}
          visibleSelected={this.state.visibleSelected}
          unpublishedSelected={this.state.unpublishedSelected}
        />
        <Restricted
          permissions={[RolePermissions.AssetsCreate, RolePermissions.AssetsManage]}
          renderContent={(hasPermission) => (
            <GenericItemsView
              className="alignment-padding"
              items={assessments}
              isLoading={isLoading}
              dataCount={totalCount}
              appliedFilter={appliedFilter}
              filterOptions={filterOptions}
              filterOptionsLoading={isFilterLoading}
              selectedIds={this.state.selectedAssessmentsIds}
              defaultSortingColumnName={this.props.shouldSort ? this.props.sortingColumnName : undefined}
              // @ts-ignore
              sortingDirection={this.props.shouldSort ? this.props.sortingDirection : undefined}
              setSortingColumnName={this.props.setSortingColumnName}
              setSortingDirection={this.props.setSortingDirection}
              fetchData={this.loadAssessmentList}
              getFilterOptions={this.getFilterOptions}
              renderSearch={this.renderSearchInput}
              setReloadListItems={this.createReloadListMethod}
              onSelectedItemChanged={this.onSelectedItemsChanged}
              onSelectionChanged={this.onSelectionChanged}
              isSelectDisabled={() => !hasPermission}
              applyFilter={this.props.applyFilter}
              resetFilter={this.props.resetFilter}
              // @ts-ignore
              getFilterForm={() => <AssessmentFilterForm />}
              noResultsContent={
                <AssessmentsNoResults
                  filtered={!isEmpty(appliedFilter) || !isEmpty(this.props.search)}
                  createButton={<CreateAssessmentButton />}
                />
              }
              columnOptions={getColumnOptions({
                handleEditClick: this.handleEditClick,
                handleDuplicateClick: this.handleDuplicateClick,
                handleDeleteClick: this.handleDeleteClick,
                handleToggleVisibility: this.handleToggleVisibility,
                visibility: !(this.state.visibleSelected && this.state.hiddenSelected),
                readonly: !hasPermission,
                deleteContentWithDependenciesFeatureEnabled: this.deleteContentWithDependenciesFeatureEnabled,
              })}
              permissions={[RolePermissions.AssetsManage]}
            />
          )}
        />
        <DeleteContentConfirmationModal
          contentType={ContentTypesEnum.Assessments}
          onTriggerRemoveContentObserver={this.onTriggerAssessmentRemovalObserver}
          onClose={this.onClearedSelection}
        />
        <AssetVisibilityConfirmationModal
          observer={this.onTriggerChangeVisibilityObserver}
          assetType="assessment"
          onClose={this.onClearedSelection}
        />
      </section>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const assessments = assessmentsStateSelector(state);
  const overview = assessments.overview;
  const entityState = assessments.base.assessmentEntityStateReducer;

  return {
    assessments: overview.assessmentsOverviewReducer.assessments,
    isLoading: overview.assessmentsOverviewReducer.isLoading || entityState.changingEntityState,
    totalCount: overview.assessmentsOverviewReducer.totalCount,
    filterOptions: assessmentFilterOptionsSelector(state),
    appliedFilter: overview.filters.appliedFilter,
    isFilterLoading: overview.filters.isLoading,
    search: assessmentSearchSelector(state),
    shouldSort: overview.filters.shouldSort,
    sortingColumnName: overview.filters.sortingColumnName,
    sortingDirection: overview.filters.sortingDirection,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  assessmentsOverviewActions: bindActionCreators(assessmentsOverviewActions, dispatch),
  entityStateActions: bindActionCreators(assessmentEntityStateActions, dispatch),
  backgroundTasksActions: bindActionCreators(backgroundTasksActions, dispatch),
  getFilterOptions: bindAction(getOverviewFilterOptions, dispatch),
  setSearch: bindAction(setSearch, dispatch),
  applyFilter: bindAction(setAppliedFilter, dispatch),
  resetFilter: bindAction(resetAppliedFilter, dispatch),
  setSortingColumnName: bindAction(setSortingColumnName, dispatch),
  setSortingDirection: bindAction(setSortingDirection, dispatch),
  setShouldSort: bindAction(setShouldSort, dispatch),
  loadAssessments: bindActionCreators(loadAssessments, dispatch),
  duplicateAssessments: bindAction(duplicateAssessments, dispatch),
  deleteAssessments: bindAction(deleteAssessments, dispatch),
  reset: () => {
    dispatch(resetAppliedFilter());
    dispatch(resetSearch());
    dispatch(resetShouldSort());
    dispatch(resetSortingColumnName());
    dispatch(resetSortingDirection());
  },
  toggleVisibility: bindAction(changeVisibility, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(withRouter(withLDConsumer()(AssessmentsOverview)));
export default ConnectedComponent;
